import React, { useState, useEffect } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Paper, Divider,
} from "@mui/material";
import { Plus, Pencil, Trash2, X, Globe, Type, Link2, AlignLeft, Tag, Eye, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetPages,
  adminGetPage,
  adminCreatePage,
  adminUpdatePage,
  adminDeletePage,
} from "../../api/pages";

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];

const STATUS_STYLES = {
  ACTIVE:   { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  DRAFT:    { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
  INACTIVE: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626" },
};

const generateSlug = (title) =>
  title.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

export default function PagesAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      title: "",
      slug: "",
      meta_title: "",
      meta_description: "",
      status: "DRAFT",
    },
  });

  const watchTitle = watch("title", "");
  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // Auto-generate slug when title changes
  useEffect(() => {
    if (watchTitle && !watch("slug")) {
      setValue("slug", generateSlug(watchTitle));
    }
  }, [watchTitle, watch, setValue]);

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ["admin-pages"],
    queryFn: () => adminGetPages().then(r => r.data),
  });

  // Fetch single page for edit
  const { data: editPageData, isLoading: isLoadingEdit } = useQuery({
    queryKey: ["admin-page", editing?.id],
    queryFn: () => adminGetPage(editing?.id).then(r => r.data),
    enabled: !!editing?.id && open,
    staleTime: 0,
  });

  // Update form when edit data loads
  useEffect(() => {
    if (editPageData && editing) {
      reset({
        title: editPageData.title || "",
        slug: editPageData.slug || "",
        meta_title: editPageData.meta_title || "",
        meta_description: editPageData.meta_description || "",
        status: editPageData.status || "DRAFT",
      });
    }
  }, [editPageData, editing, reset]);

  const createMutation = useMutation({
    mutationFn: adminCreatePage,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-pages"]); 
      notify("Page created"); 
      closeDialog(); 
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create page", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdatePage(id, data),
    onSuccess: () => { 
      qc.invalidateQueries(["admin-pages"]); 
      qc.invalidateQueries(["admin-page", editing?.id]);
      notify("Page updated"); 
      closeDialog(); 
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update page", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeletePage,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-pages"]); 
      notify("Page deleted"); 
      setConfirmDelete(null); 
    },
    onError: (err) => { 
      notify(err?.response?.data?.message || "Couldn't delete page", "error"); 
      setConfirmDelete(null); 
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const payload = {
      ...values,
      slug: values.slug?.trim() || generateSlug(values.title),
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  const openCreate = () => {
    setEditing(null);
    reset({
      title: "",
      slug: "",
      meta_title: "",
      meta_description: "",
      status: "DRAFT"
    });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false); 
    setEditing(null); 
    reset();
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter pages based on search query
  const filteredPages = pages.filter(p =>
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.meta_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.meta_description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const startIndex = page * rowsPerPage;
  const paginatedPages = filteredPages.slice(startIndex, startIndex + rowsPerPage);

  return (
    <AdminShell title="Pages">
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Static and dynamic pages published on the public site. Manage slugs, meta tags, and visibility.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", 
            textTransform: "none", 
            fontWeight: 700, 
            bgcolor: "#2563EB", 
            boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)", 
            flexShrink: 0,
            "&:hover": { bgcolor: "#1D4ED8" }
          }}
        >
          Add Page
        </Button>
      </Box>

      {/* Search bar */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Search size={18} color="#94A3B8" />
          <TextField
            fullWidth
            placeholder="Search pages by title, slug, or meta data..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{
              sx: { fontSize: 13.5 }
            }}
          />
        </Box>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Page", "Slug", "Meta Title", "Sections", "Status", ""].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase", color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 180 : j === 1 ? 120 : 90} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedPages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <Globe size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>No pages found</Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search." : "Create your first page to publish it on the public site."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedPages.map((p) => {
                const statusStyle = STATUS_STYLES[p.status] || STATUS_STYLES.DRAFT;
                return (
                  <TableRow key={p.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    {/* Page column */}
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Globe size={16} strokeWidth={2.1} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                            {p.title}
                          </Typography>
                          {p.meta_description && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }} noWrap>
                              {p.meta_description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Slug */}
                    <TableCell>
                      {p.slug ? (
                        <Typography sx={{ fontSize: 12.5, fontFamily: "monospace", color: "rgba(15,23,42,0.5)", bgcolor: "rgba(15,23,42,0.04)", px: 1, py: 0.35, borderRadius: "6px", display: "inline-block", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          /{p.slug}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Meta title */}
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography sx={{ fontSize: 13, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.meta_title || "—"}
                      </Typography>
                    </TableCell>

                    {/* Sections */}
                    <TableCell>
                      {p.sections?.length > 0 ? (
                        <Chip label={`${p.sections.length} section${p.sections.length !== 1 ? "s" : ""}`} size="small" sx={{ fontWeight: 600, fontSize: 11, height: 22, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB" }} />
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "rgba(15,23,42,0.35)", fontStyle: "italic" }}>None</Typography>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip label={p.status || "DRAFT"} size="small" sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`/api/pages/${p.slug}/`, "_blank")}
                            sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#06B6D4", color: "#0891B2", bgcolor: "rgba(6,182,212,0.05)" } }}
                          >
                            <Eye size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(p)} sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" } }}>
                            <Pencil size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setConfirmDelete(p)} sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" } }}>
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        
        {/* Pagination */}
        {!isLoading && filteredPages.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredPages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": {
                pl: 2, pr: 1,
              },
              "& .MuiTablePagination-selectLabel": {
                fontSize: 13.5,
                color: "text.secondary",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: 13.5,
                color: "text.secondary",
              },
            }}
          />
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog 
        open={open} 
        onClose={closeDialog} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ 
          sx: { 
            borderRadius: "18px",
            overflow: "hidden" 
          } 
        }}
      >
        {/* Gradient header */}
        <Box sx={{ 
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          px: 3, 
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "white" }}>
              {editing ? "Edit Page" : "New Page"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
              {editing ? "Update the page details and SEO metadata." : "Set up the page title, slug, and SEO metadata."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDialog} disabled={saving} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <X size={20} />
          </IconButton>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
            {isLoadingEdit ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {/* Basic Information Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Type size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Basic Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="title"
                      control={control}
                      rules={{ required: "Title is required" }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Title"
                          fullWidth
                          disabled={saving}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Type size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="slug"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Slug"
                          fullWidth
                          disabled={saving}
                          placeholder={watchTitle ? generateSlug(watchTitle) : "auto-generated from title"}
                          helperText="Leave blank to auto-generate. Lowercase letters, numbers, hyphens only."
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Link2 size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                  </Box>
                </Card>

                {/* SEO Metadata Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Search size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      SEO Metadata
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="meta_title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Meta Title"
                          fullWidth
                          disabled={saving}
                          placeholder="Defaults to page title if blank"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Type size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="meta_description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Meta Description"
                          fullWidth
                          multiline
                          rows={3}
                          disabled={saving}
                          placeholder="150–160 characters recommended"
                          InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}><AlignLeft size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                  </Box>
                </Card>

                {/* Status Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Tag size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Status
                    </Typography>
                  </Box>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Status"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Tag size={15} color="#94A3B8" /></InputAdornment>
                        }}
                      >
                        {STATUSES.map(s => (
                          <MenuItem key={s} value={s}>
                            <Chip label={s} size="small" sx={{ fontWeight: 600, fontSize: 12, height: 20, bgcolor: STATUS_STYLES[s]?.bg || "rgba(107,114,128,0.1)", color: STATUS_STYLES[s]?.color || "#6B7280" }} />
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Card>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
            <Button
              type="submit" 
              variant="contained" 
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
              sx={{ 
                textTransform: "none", 
                fontWeight: 700, 
                borderRadius: "10px", 
                bgcolor: "#2563EB", 
                px: 2.5, 
                minWidth: 100,
                "&:hover": { bgcolor: "#1D4ED8" }
              }}
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => !deleteMutation.isPending && setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this page?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.title}" will be removed from the public site immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending} 
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ 
              textTransform: "none", 
              fontWeight: 700, 
              borderRadius: "10px", 
              bgcolor: "#EF4444", 
              px: 2.5, 
              minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}